/**
 * ALTO Collection Scroll Restore
 *
 * Restores the user's place on a product grid after a browser back/forward
 * navigation from a product page: re-loads the pages they had loaded and scrolls
 * the product card they clicked back into view.
 *
 * Used by both alto-main-collection-product-grid and alto-search-results, which
 * share the same `#product-grid` + <alto-collection-pagination> structure.
 *
 * - Only acts on genuine back/forward navigation. Fresh visits (nav menu, direct
 *   link, new search, filter change) reset to page 1.
 * - Never touches the URL and never listens to `popstate` — Dawn's facets.js owns
 *   filter/URL state via popstate/pushState; this layer stays out of its way.
 * - When the browser restores from bfcache (`pageshow.persisted`), the DOM and
 *   scroll position are already intact, so this does nothing.
 *
 * Depends on <alto-collection-pagination> exposing `pagesLoaded`, `perPage` and
 * `restoreToPage()` (see alto-collection-pagination.js).
 */
(function () {
  const STORAGE_KEY = 'alto:collection-restore';
  const STALE_MS = 30 * 60 * 1000; // 30 minutes
  const GRID_ID = 'product-grid';

  // Manage scroll ourselves so the browser doesn't fight the restore.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  const storage = (() => {
    try {
      const test = '__alto_test__';
      window.sessionStorage.setItem(test, '1');
      window.sessionStorage.removeItem(test);
      return window.sessionStorage;
    } catch (e) {
      return null;
    }
  })();

  const currentKey = () => window.location.pathname + window.location.search;
  const pagination = () => document.querySelector('alto-collection-pagination');
  const grid = () => document.getElementById(GRID_ID);

  function clearSnapshot() {
    storage?.removeItem(STORAGE_KEY);
  }

  function readSnapshot() {
    if (!storage) return null;
    try {
      return JSON.parse(storage.getItem(STORAGE_KEY));
    } catch (e) {
      return null;
    }
  }

  // --- Save: user clicks a product card, about to leave the collection --------

  function saveSnapshot(link) {
    if (!storage) return;
    const card = link.closest('.alto-product-card[data-product-id]');
    const pag = pagination();
    const snapshot = {
      key: currentKey(),
      pagesLoaded: pag ? pag.pagesLoaded : 1,
      productsPerPage: pag ? pag.perPage : grid()?.children.length || 0,
      clickedProductId: card ? card.dataset.productId : null,
      scrollY: window.scrollY,
      savedAt: Date.now(),
    };
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
    } catch (e) {
      /* quota or disabled — restore simply won't run */
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest(`#${GRID_ID} .alto-product-card a[href]`);
      if (link) saveSnapshot(link);
    },
    true // capture: run before the navigation begins
  );

  // --- Restore: arriving back at the collection -------------------------------

  function isBackForward() {
    const entry = performance.getEntriesByType?.('navigation')?.[0];
    if (entry && entry.type) return entry.type === 'back_forward';
    // Legacy fallback (deprecated API): 2 === TYPE_BACK_FORWARD
    return performance.navigation && performance.navigation.type === 2;
  }

  // The overlay is rendered (hidden) in the section markup and reuses the shared
  // ALTO/Dawn loading spinner; we only toggle its visibility here.
  const overlayEl = () => document.querySelector('[data-restore-overlay]');

  function showOverlay() {
    const el = overlayEl();
    if (!el) return;
    el.classList.remove('tw:hidden');
    el.removeAttribute('aria-hidden');
    el.setAttribute('aria-busy', 'true');
  }

  function hideOverlay() {
    const el = overlayEl();
    if (!el) return;
    el.classList.add('tw:hidden');
    el.setAttribute('aria-hidden', 'true');
    el.removeAttribute('aria-busy');
  }

  function scrollToTarget(snapshot) {
    const card =
      snapshot.clickedProductId &&
      grid()?.querySelector(`.alto-product-card[data-product-id="${snapshot.clickedProductId}"]`);
    requestAnimationFrame(() => {
      if (card) {
        card.scrollIntoView({ block: 'center' });
      } else if (typeof snapshot.scrollY === 'number') {
        window.scrollTo(0, snapshot.scrollY);
      }
    });
  }

  async function restore(snapshot) {
    const pag = pagination();
    const needsFetch = pag && snapshot.pagesLoaded > pag.pagesLoaded;
    if (needsFetch) showOverlay();
    try {
      if (needsFetch) await pag.restoreToPage(snapshot.pagesLoaded);
    } catch (err) {
      console.error('Scroll restore: page rebuild failed:', err);
    } finally {
      hideOverlay();
    }
    scrollToTarget(snapshot);
  }

  function handlePageShow(event) {
    // bfcache restored the full page (DOM + scroll) — nothing to do.
    if (event.persisted) {
      clearSnapshot();
      return;
    }

    const snapshot = readSnapshot();
    clearSnapshot(); // one-shot: consume regardless of whether we restore

    if (!snapshot) return;
    if (!isBackForward()) return; // fresh visit → page 1
    if (snapshot.key !== currentKey()) return; // different collection/filters
    if (!snapshot.savedAt || Date.now() - snapshot.savedAt > STALE_MS) return; // stale

    restore(snapshot);
  }

  window.addEventListener('pageshow', handlePageShow);
})();
